import { LoaderFunction, useLoaderData } from "react-router-dom";
import {
  Book,
  Course,
  CourseDetails,
  getCourseDetails,
  getTerms,
  Term,
  TermData,
} from "common/api/decimal/methods/course";
import { DecimalResult } from "../../../../common/api/decimal/utils.ts";
import {
  getBatchProductDetails,
  Product,
} from "../../../../common/api/decimal/methods/product.ts";
import { loadBrandOnce } from "../../../root/rootLoader.ts";
import { memoize } from "../../../../common/utils/function.ts";
import { getCoursesExtras } from "routes/home/homeContext.ts";
import {
  CompareCart,
  getCart,
} from "common/api/decimal/methods/compareAndPurchase.ts";

export const UNMATCHED_TERM_ID = "unmatched";

export type TermLite = Omit<Term, "courses">;

export interface CourseData {
  courses: ExpandedCourse[];
  materialCount: number;
  allMaterials: CourseMaterial[];
  currentTerm: TermLite | undefined;
  terms: TermIdentifier[];
  compareCart?: CompareCart;
}

export type CourseMaterial = Book & {
  courseId: number | string;
  // in virtual store mode this gives us pricing info
  product?: Product[];
};

export type ExpandedCourse = Omit<CourseDetails, "books"> & {
  courseMaterials: CourseMaterial[];
};

export type TermIdentifier = Pick<Term, "id" | "displayName">;

export const buildTermIdentifiers = (terms: TermData): TermIdentifier[] => {
  const identifiers = terms.terms.map((term: Term) => ({
    id: term.id,
    displayName: term.displayName,
  }));
  if (terms.unmatchedCourses?.length) {
    identifiers.push({
      id: UNMATCHED_TERM_ID,
      displayName: "",
    });
  }
  return identifiers;
};

const findTermById = (
  terms: Term[],
  termId?: string | number | null
): Term | undefined => {
  if (termId === undefined || termId === null) {
    return undefined;
  }
  return terms.find((term: Term) => termId.toString() === term.id.toString());
};

// Return the specified term if valid, otherwise return the default term, the first term, or the unmatched term id if available
export const findTermIdToShow = (
  termId: string | null,
  { terms, unmatchedCourses }: TermData,
  defaultTermId?: string | number
): string | number | undefined => {
  const hasUnmatchedCourses = unmatchedCourses.length > 0;
  if (hasUnmatchedCourses && termId === UNMATCHED_TERM_ID) {
    return UNMATCHED_TERM_ID;
  }
  const id = findTermById(terms, termId)?.id;
  if (id) {
    return id;
  }
  const defaultId = findTermById(terms, defaultTermId)?.id ?? terms[0]?.id;
  if (defaultId) {
    return defaultId;
  }
  if (hasUnmatchedCourses) {
    return UNMATCHED_TERM_ID;
  }
};

export function useCoursesLoaderData(): CourseData {
  return useLoaderData() as CourseData;
}

interface CoursePartition {
  currentTerm: TermLite | null;
  courses: Course[];
}

const partitionCourses = (
  term: Term | null,
  unmatchedCourses: Course[]
): CoursePartition => {
  if (!term) {
    return {
      currentTerm: null,
      courses: unmatchedCourses,
    };
  }
  const { courses, ...rest } = term;
  return {
    currentTerm: { ...rest },
    courses,
  };
};

export const getBatchProductDetailsMemo = memoize((vbids: string[]) =>
  getBatchProductDetails(vbids)
);

const bookNeedsPricing = (book: Book) => {
  return (
    book.buttonLinks.some((link) => link.type === "ePOS") ||
    book.purchaseLinks.some(
      (link) => link.type === "opt_in" || link.type === "opt_out"
    )
  );
};

const getCompareCart = (term: Term | null) => {
  if (!term?.hasCompareCart) {
    return Promise.resolve(null);
  }
  return getCart().then(({ data }) => data);
};

export const loadCourses: LoaderFunction = ({ request }) => {
  const searchParams = new URL(request.url).searchParams;

  const termIdParam = searchParams.get("termId");
  return Promise.all([getTerms(), getCoursesExtras(), loadBrandOnce()]).then(
    ([{ data }, { defaultTermId }, brand]) => {
      if (!data) {
        return Promise.reject(
          `No data found for Term with id of ${termIdParam}`
        );
      }
      const isVirtualStore = !!brand.VirtualStore?.companyId;
      const termId = findTermIdToShow(termIdParam, data, defaultTermId);
      if (!termId) {
        const statusText = termIdParam
          ? `No Term data found with id of ${termIdParam}.`
          : "No Term data to show.";
        throw new Response("Not Found", {
          status: 404,
          statusText,
        });
      }
      const term =
        termId === UNMATCHED_TERM_ID ? null : findTermById(data.terms, termId)!;
      const { currentTerm, courses } = partitionCourses(
        term,
        data.unmatchedCourses
      );
      const courseDetailPromises: Promise<DecimalResult<CourseDetails>>[] =
        courses.map((course: Course) => getCourseDetails(course.id, termId));
      const allMaterials: CourseMaterial[] = [];

      return Promise.all([
        Promise.all(courseDetailPromises),
        getCompareCart(term),
      ]).then(([results, compareCart]) => {
        const expandedCourses = results
          .map(({ data }) => data)
          .reduce(
            (
              final: ExpandedCourse[],
              courseDetails: CourseDetails | undefined
            ) => {
              if (!courseDetails) {
                return final;
              }
              const courseMaterials =
                courseDetails.books?.map((courseMaterial: Book) => ({
                  ...courseMaterial,
                  courseId: courseDetails.id,
                })) ?? [];
              allMaterials.push(...courseMaterials);
              const { books: _, ...rest } = courseDetails;
              final.push({
                ...rest,
                courseMaterials,
              });
              return final;
            },
            []
          );
        // this is temp because currently server is sending empty button links
        if (isVirtualStore) {
          allMaterials.forEach((material) => {
            const status = material.materialStatus;
            if (
              ((status === "not_included" &&
                !material.buttonLinks.find(
                  (link) => link.type === "open_book"
                )) ||
                status === "opted_out") &&
              !material.buttonLinks.find((link) => link.type === "ePOS")
            ) {
              material.buttonLinks.push({
                type: "ePOS",
                link: "",
              });
            }
          });
        }
        // ideally this would be done server side, ticket VST-87052, in the interim clientside
        const vbids =
          isVirtualStore && allMaterials.length
            ? allMaterials
                .filter((book) => bookNeedsPricing(book))
                .map((book) => book.vbid)
            : [];
        // in term switching scenario, keep the pricing unless term looks to have changed
        const pricing = vbids.length
          ? getBatchProductDetailsMemo(vbids)
          : Promise.resolve({} as ReturnType<typeof getBatchProductDetails>);

        return pricing.then((products) => {
          allMaterials.forEach((material) => {
            material.product = products[material.vbid];
          });
          return {
            terms: buildTermIdentifiers(data),
            currentTerm: currentTerm,
            courses: expandedCourses,
            materialCount: allMaterials.length,
            allMaterials,
            compareCart: compareCart,
          } as CourseData;
        });
      });
    }
  );
};
